import { useState, useEffect, useMemo } from "react";
import {
  collection,
  getDocs,
  doc,
  setDoc,
  updateDoc,
  query,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "@/firebase";
import {
  X,
  Loader2,
  Search,
  Upload,
  FileText,
  CheckCircle2,
  AlertTriangle,
  UserPlus,
  Phone,
  Mail,
  Copy,
  Check,
  ExternalLink,
  MessageSquare,
  Send,
  Share2,
  Building2,
  Sparkles,
} from "lucide-react";
import { isEmailDomainAllowed } from "./helpers";
import {
  syncWebUser,
  getWhatsAppChatUrl,
  WE360_WHATSAPP_BOT_NUMBER,
  COUNTRY_CODES,
  normalizeWhatsAppPhone,
  validateWhatsAppPhone,
} from "@/services/coreServicesClient";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Parser para CSV con soporte para email, nombre, apellido y teléfono/whatsapp
function parseCsv(text) {
  const firstLine = (text.split(/\r?\n/)[0] || "").trim();
  const delim = firstLine.includes(";") ? ";" : firstLine.includes("\t") ? "\t" : ",";
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter((l) => l.length > 0);
  if (lines.length === 0) return { rows: [], headerDetected: false };
  const firstFields = lines[0].split(delim).map((s) => s.trim().toLowerCase());
  const headerDetected = firstFields.some((f) => f === "email" || f === "correo" || f === "mail");
  let header = ["email", "nombre", "apellido", "phone"];
  let dataLines = lines;
  if (headerDetected) {
    header = firstFields.map((h) => {
      if (h === "correo" || h === "mail") return "email";
      if (h === "nombres" || h === "name" || h === "firstname") return "nombre";
      if (h === "apellidos" || h === "lastname") return "apellido";
      if (h === "whatsapp" || h === "telefono" || h === "celular" || h === "phone" || h === "movil") return "phone";
      return h;
    });
    dataLines = lines.slice(1);
  }
  const rows = dataLines.map((line, idx) => {
    const fields = line.split(delim).map((s) => s.trim().replace(/^"|"$/g, ""));
    const row = { _line: idx + (headerDetected ? 2 : 1) };
    header.forEach((h, i) => { row[h] = fields[i] || ""; });
    if (!headerDetected && fields.length === 1 && !row.email) row.email = fields[0];
    return row;
  });
  return { rows, headerDetected, delimiter: delim };
}

// ═══════════════════════════════════════════════════════════
// Associate Users Modal — for client admin (B2C only)
// ═══════════════════════════════════════════════════════════
export function AssociateUsersModal({
  client,
  allUsers, // when null, fetch B2C users from Firestore
  allowedDomains,
  ignoreDomainRestriction = false,
  professionalsOnly = false,
  onClose,
  onCompleted,
}) {
  const [pool, setPool] = useState(allUsers || []);
  const [loading, setLoading] = useState(allUsers === null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Fetch B2C users on mount when in client-admin mode
  useEffect(() => {
    if (allUsers !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, "users"));
        if (cancelled) return;
        let list = snap.docs
          .map((d) => ({ uid: d.id, ...d.data() }))
          .filter((u) => !u.clientId); // B2C only
        if (professionalsOnly) {
          list = list.filter((u) => u.isMedico === true || u.role === "medico" || u.isIHO === true || u.role === "iho");
        }
        setPool(list);
      } catch (err) {
        console.error("Error fetching B2C users:", err);
        setError("No se pudieron cargar los usuarios disponibles.");
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [allUsers, professionalsOnly]);

  const filtered = useMemo(() => {
    let list = pool;
    if (professionalsOnly && allUsers !== null) {
      list = list.filter((u) => u.isMedico === true || u.role === "medico" || u.isIHO === true || u.role === "iho");
    }
    return list.filter((u) => {
      const email = (u.email || u.profile?.email || "").toLowerCase();
      const name = (u.displayName || u.profile?.name || "").toLowerCase();
      if (!ignoreDomainRestriction && !professionalsOnly && !isEmailDomainAllowed(email, allowedDomains)) {
        return false;
      }
      if (!search) return true;
      const s = search.toLowerCase();
      return email.includes(s) || name.includes(s);
    });
  }, [pool, search, allowedDomains, ignoreDomainRestriction, professionalsOnly, allUsers]);

  const toggle = (uid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((u) => u.uid)));
  };

  const confirm = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    setError(null);
    try {
      const batch = writeBatch(db);
      selected.forEach((uid) => {
        batch.update(doc(db, "users", uid), { clientId: client.id });
      });
      await batch.commit();
      onCompleted();
    } catch (err) {
      console.error("Error associating users:", err);
      setError(err.message || "Error al asociar los usuarios.");
      setBusy(false);
    }
  };

  return (
    <ModalShell
      title={professionalsOnly ? `Asociar Médico/IHO a ${client.name}` : `Asociar usuarios B2C a ${client.name}`}
      subtitle={
        professionalsOnly
          ? "Selecciona profesionales de la salud independientes para asignarlos a esta empresa"
          : allowedDomains?.length > 0 && !ignoreDomainRestriction
          ? `Solo se muestran usuarios con dominio: ${allowedDomains.map((d) => `@${d}`).join(", ")}`
          : "Selecciona usuarios sin empresa para agregarlos"
      }
      onClose={onClose}
      testId={professionalsOnly ? "associate-professional-modal" : "associate-users-modal"}
    >
      <div className="relative mb-3">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#94A3B8" }} />
        <input
          type="text"
          placeholder="Buscar por email o nombre..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm"
          style={{ borderColor: "#E2E8F0" }}
          data-testid="associate-search"
        />
      </div>

      <div className="border rounded-lg overflow-hidden" style={{ borderColor: "#E2E8F0" }}>
        <div className="bg-gray-50 px-3 py-2 border-b flex items-center justify-between text-xs" style={{ borderColor: "#E2E8F0", color: "#64748B" }}>
          <span>{filtered.length} disponibles</span>
          {filtered.length > 0 && (
            <button onClick={selectAll} className="text-xs font-semibold hover:underline cursor-pointer" style={{ color: "#0284C7", background: "none", border: "none" }}>
              {selected.size === filtered.length ? "Deseleccionar todos" : "Seleccionar todos"}
            </button>
          )}
        </div>

        <div className="overflow-y-auto max-h-[320px]">
          {loading ? (
            <div className="text-center py-8">
              <Loader2 size={20} className="mx-auto animate-spin" style={{ color: "#94A3B8" }} />
              <p className="text-xs mt-2" style={{ color: "#94A3B8" }}>Cargando usuarios...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8 text-sm" style={{ color: "#94A3B8" }}>
              {allowedDomains?.length > 0
                ? `No hay usuarios con dominio permitido (${allowedDomains.join(", ")}).`
                : "No hay usuarios disponibles."}
            </div>
          ) : (
            filtered.map((u) => {
              const email = u.email || u.profile?.email || "";
              const name = u.displayName || u.profile?.name || "";
              return (
                <label
                  key={u.uid}
                  className="flex items-center gap-3 px-3 py-2 border-t cursor-pointer hover:bg-gray-50"
                  style={{ borderColor: "#F1F5F9" }}
                  data-testid={`associate-row-${u.uid}`}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(u.uid)}
                    onChange={() => toggle(u.uid)}
                    className="w-4 h-4 rounded cursor-pointer"
                    style={{ accentColor: "#0284C7" }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate" style={{ color: "#1E293B" }}>
                      {email || "(sin email)"}
                    </div>
                    {name && <div className="text-[11px] truncate" style={{ color: "#94A3B8" }}>{name}</div>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {u.isIHO && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#EFF6FF", color: "#1D4ED8", border: "1px solid #BFDBFE" }}>
                        IHO
                      </span>
                    )}
                    {(u.isMedico || u.role === "medico") && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#ECFDF5", color: "#0D9488", border: "1px solid #A7F3D0" }}>
                        Médico
                      </span>
                    )}
                    {!u.isIHO && !u.isMedico && u.role !== "medico" && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#DCFCE7", color: "#166534" }}>
                        B2C
                      </span>
                    )}
                  </div>
                </label>
              );
            })
          )}
        </div>
      </div>

      {error && (
        <div className="mt-3 p-3 rounded-lg text-sm" style={{ background: "#FEE2E2", color: "#991B1B" }}>
          {error}
        </div>
      )}

      <div className="flex gap-2 mt-4 justify-end">
        <button
          onClick={onClose}
          className="px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer"
          style={{ background: "#F1F5F9", color: "#475569", border: "none" }}
          data-testid="associate-cancel"
        >
          Cancelar
        </button>
        <button
          onClick={confirm}
          disabled={busy || selected.size === 0}
          className="px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer flex items-center gap-2"
          style={{
            background: "#0284C7",
            color: "white",
            border: "none",
            opacity: busy || selected.size === 0 ? 0.5 : 1,
            cursor: busy || selected.size === 0 ? "not-allowed" : "pointer",
          }}
          data-testid="associate-confirm"
        >
          {busy && <Loader2 size={14} className="animate-spin" />}
          Asociar {selected.size > 0 ? selected.size : ""} {selected.size === 1 ? "usuario" : "usuarios"}
        </button>
      </div>
    </ModalShell>
  );
}

// ═══════════════════════════════════════════════════════════
// Upload Employees Modal (CSV) — Sincronizado con Core Services
// ═══════════════════════════════════════════════════════════
export function UploadEmployeesModal({
  client,
  allowedDomains,
  onClose,
  onCompleted,
}) {
  const [step, setStep] = useState("upload");
  const [parsed, setParsed] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);

  const onFile = async (file) => {
    setError(null);
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { setError("Archivo demasiado grande (máx 2MB)"); return; }
    try {
      const text = await file.text();
      const { rows } = parseCsv(text);

      const snap = await getDocs(collection(db, "users"));
      const usersByEmail = new Map();
      snap.docs.forEach((d) => {
        const u = { uid: d.id, ...d.data() };
        const e = (u.email || u.profile?.email || "").toLowerCase().trim();
        if (e) usersByEmail.set(e, u);
      });

      const classified = {
        valid: [],         // pending_assignment
        existingFree: [],  // assign directly
        sameClient: [],    // already in this client
        domainBlocked: [], // wrong domain
        otherClient: [],   // already in other client
        invalid: [],
      };
      const seen = new Set();
      rows.forEach((r) => {
        const email = (r.email || "").toLowerCase().trim();
        if (!email || !EMAIL_RE.test(email)) {
          classified.invalid.push({ ...r, email, reason: !email ? "Email vacío" : "Email inválido" });
          return;
        }
        if (seen.has(email)) {
          classified.invalid.push({ ...r, email, reason: "Duplicado en el archivo" });
          return;
        }
        seen.add(email);
        if (!isEmailDomainAllowed(email, allowedDomains)) {
          classified.domainBlocked.push({ ...r, email });
          return;
        }
        const existing = usersByEmail.get(email);
        if (existing) {
          if (existing.clientId === client.id) classified.sameClient.push({ ...r, email });
          else if (existing.clientId) classified.otherClient.push({ ...r, email, user: existing });
          else classified.existingFree.push({ ...r, email, user: existing });
        } else {
          classified.valid.push({ ...r, email });
        }
      });
      setParsed({ rows, classified });
      setStep("preview");
    } catch (err) {
      console.error(err);
      setError(err.message || "No se pudo leer el archivo");
    }
  };

  const confirm = async () => {
    if (!parsed) return;
    setBusy(true);
    setError(null);
    const { classified } = parsed;
    const summary = {
      created: 0,
      assigned: 0,
      skippedSame: classified.sameClient.length,
      skippedOther: classified.otherClient.length,
      skippedDomain: classified.domainBlocked.length,
      invalid: classified.invalid.length,
    };
    try {
      const batch = writeBatch(db);
      const usersToSync = [];

      classified.valid.forEach((r) => {
        const ref = doc(db, "pending_assignments", r.email);
        batch.set(ref, {
          email: r.email,
          clientId: client.id,
          clientName: client.name,
          firstName: r.nombre || "",
          lastName: r.apellido || "",
          phone: r.phone || "",
          createdAt: new Date().toISOString(),
        });
        summary.created++;

        usersToSync.push({
          name: `${r.nombre || ""} ${r.apellido || ""}`.trim() || r.email,
          firstName: r.nombre || "",
          lastName: r.apellido || "",
          email: r.email,
          phone: r.phone || "",
          companyId: client.id,
          businessModel: "B2B",
        });
      });

      classified.existingFree.forEach((r) => {
        batch.update(doc(db, "users", r.user.uid), { clientId: client.id });
        summary.assigned++;
      });

      await batch.commit();

      // Sincronizar en lote a WE360 Core Services (Fase 6: COMPLETED_ACTIVE_COACHING)
      if (usersToSync.length > 0) {
        try {
          await syncWebUser({
            companyId: client.id,
            businessModel: "B2B",
            users: usersToSync,
          });
        } catch (syncErr) {
          console.warn("[UploadEmployeesModal] Warning syncing with Core Services:", syncErr);
        }
      }

      setResult(summary);
      setStep("done");
    } catch (err) {
      console.error("Error in batch upload:", err);
      setError(err.message || "Error al guardar las asignaciones");
    }
    setBusy(false);
  };

  const registerLink = `${typeof window !== "undefined" ? window.location.origin : ""}/register?empresa=${encodeURIComponent(client.id || client.name)}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(registerLink);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
  };

  return (
    <ModalShell
      title={`Cargar colaboradores a ${client.name}`}
      subtitle={
        allowedDomains?.length > 0
          ? `Solo se aceptan emails con dominio: ${allowedDomains.map((d) => `@${d}`).join(", ")}`
          : "Sube un archivo CSV con columnas: email,nombre,apellido,whatsapp"
      }
      onClose={onClose}
      testId="upload-employees-modal"
      wide
    >
      {step === "upload" && (
        <div data-testid="upload-step-1">
          <div className="border-2 border-dashed rounded-2xl p-8 text-center bg-slate-50/60 border-slate-300">
            <FileText size={36} className="mx-auto mb-2 text-slate-400" />
            <p className="text-sm font-bold mb-1 text-slate-800">Selecciona o arrastra un archivo CSV</p>
            <p className="text-xs mb-4 text-slate-500">
              Formato recomendado: <code>email,nombre,apellido,whatsapp</code>
            </p>
            <input
              type="file"
              accept=".csv,.txt"
              onChange={(e) => onFile(e.target.files?.[0])}
              className="text-sm file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-[#00628F] file:text-white hover:file:opacity-90 cursor-pointer"
              data-testid="upload-csv-input"
            />
          </div>
          {error && (
            <div className="mt-3 p-3 rounded-xl text-sm bg-red-50 text-red-800 border border-red-200">
              {error}
            </div>
          )}
        </div>
      )}

      {step === "preview" && parsed && (
        <div data-testid="upload-step-2">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
            <Stat label="Nuevos" value={parsed.classified.valid.length} color="#16A34A" testId="preview-stat-new" />
            <Stat label="Asignar" value={parsed.classified.existingFree.length} color="#0284C7" testId="preview-stat-assign" />
            <Stat label="Otra empresa" value={parsed.classified.otherClient.length} color="#F9A01B" testId="preview-stat-other" />
            <Stat label="Sin dominio" value={parsed.classified.domainBlocked.length} color="#9333EA" testId="preview-stat-domain" />
            <Stat label="Inválidos" value={parsed.classified.invalid.length} color="#C0453A" testId="preview-stat-invalid" />
          </div>

          <div className="overflow-y-auto max-h-[300px] border rounded-xl border-slate-200">
            {parsed.classified.valid.length > 0 && (
              <Section title="Cuentas nuevas (se pre-aprovisionarán en Core Services)" color="#16A34A" icon={CheckCircle2}>
                {parsed.classified.valid.map((r) => (
                  <Row key={r.email} email={r.email} extra={`${r.nombre || ""} ${r.apellido || ""} ${r.phone ? `• ${r.phone}` : ""}`.trim()} />
                ))}
              </Section>
            )}
            {parsed.classified.existingFree.length > 0 && (
              <Section title="Usuarios existentes (se asignarán a la empresa)" color="#0284C7" icon={CheckCircle2}>
                {parsed.classified.existingFree.map((r) => <Row key={r.email} email={r.email} extra={r.user.displayName || ""} />)}
              </Section>
            )}
            {parsed.classified.otherClient.length > 0 && (
              <Section title="Ya en otra empresa (se ignorarán)" color="#F9A01B" icon={AlertTriangle}>
                {parsed.classified.otherClient.map((r) => <Row key={r.email} email={r.email} extra="Pide al admin WE360 reasignar" />)}
              </Section>
            )}
            {parsed.classified.domainBlocked.length > 0 && (
              <Section title="Dominio no permitido (se ignorarán)" color="#9333EA" icon={AlertTriangle}>
                {parsed.classified.domainBlocked.map((r) => <Row key={r.email} email={r.email} extra="Fuera del dominio corporativo" />)}
              </Section>
            )}
            {parsed.classified.sameClient.length > 0 && (
              <Section title="Ya en esta empresa (se ignorarán)" color="#94A3B8" icon={CheckCircle2}>
                {parsed.classified.sameClient.map((r) => <Row key={r.email} email={r.email} />)}
              </Section>
            )}
            {parsed.classified.invalid.length > 0 && (
              <Section title="Inválidos" color="#C0453A" icon={AlertTriangle}>
                {parsed.classified.invalid.map((r, i) => <Row key={`${r.email}-${i}`} email={r.email || "(vacío)"} extra={r.reason} />)}
              </Section>
            )}
          </div>

          {error && (
            <div className="mt-3 p-3 rounded-xl text-sm bg-red-50 text-red-800 border border-red-200">
              {error}
            </div>
          )}

          <div className="flex gap-2 mt-4 justify-end">
            <button
              onClick={() => { setParsed(null); setStep("upload"); }}
              className="px-4 py-2 rounded-xl text-sm font-semibold cursor-pointer bg-slate-100 text-slate-700 hover:bg-slate-200"
              data-testid="upload-back-btn"
            >
              Atrás
            </button>
            <button
              onClick={confirm}
              disabled={busy || (parsed.classified.valid.length === 0 && parsed.classified.existingFree.length === 0)}
              className="px-4 py-2 rounded-xl text-sm font-semibold cursor-pointer flex items-center gap-2 text-white shadow-sm"
              style={{ background: "#00628F", opacity: busy ? 0.6 : 1 }}
              data-testid="upload-confirm-btn"
            >
              {busy && <Loader2 size={14} className="animate-spin" />}
              Confirmar e invitar
            </button>
          </div>
        </div>
      )}

      {step === "done" && result && (
        <div className="text-center py-5" data-testid="upload-step-3">
          <div className="w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-3">
            <CheckCircle2 size={32} />
          </div>
          <h3 className="text-xl font-extrabold text-slate-900">¡Carga y Sincronización Exitosa!</h3>
          <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
            Los colaboradores han quedado pre-aprovisionados en el Core (Fase 6). Pueden acceder vía web o escribirle directamente al Asistente de WhatsApp.
          </p>

          <div className="grid grid-cols-2 gap-2.5 max-w-md mx-auto mt-4 text-left text-xs">
            <ResultRow label="Nuevos pre-aprovisionados" value={result.created} color="#16A34A" />
            <ResultRow label="Asignados directamente" value={result.assigned} color="#0284C7" />
            <ResultRow label="Ya registrados en la empresa" value={result.skippedSame} color="#94A3B8" />
            <ResultRow label="Omitidos / Inválidos" value={result.skippedDomain + result.invalid} color="#C0453A" />
          </div>

          {/* Opciones de Invitación Inmediata */}
          <div className="mt-6 p-4 rounded-2xl bg-slate-50 border border-slate-200 max-w-lg mx-auto text-left">
            <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Sparkles size={13} className="text-[#00628F]" /> Opciones de Invitación para RRHH
            </h4>
            
            <div className="flex flex-col sm:flex-row gap-2 mt-3">
              <button
                type="button"
                onClick={handleCopyLink}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-xs font-bold border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 transition-all cursor-pointer"
                data-testid="btn-copy-register-link"
              >
                {copiedLink ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
                <span>{copiedLink ? "¡Enlace Copiado!" : "Copiar Enlace Web"}</span>
              </button>

              <a
                href={getWhatsAppChatUrl(undefined, `Hola, soy colaborador de ${client.name} y deseo activar mi cuenta de bienestar`)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-xs font-bold text-white bg-[#25D366] hover:bg-[#20ba5a] transition-all shadow-sm cursor-pointer"
                data-testid="btn-open-bot-whatsapp"
              >
                <MessageSquare size={14} />
                <span>Bot de WhatsApp</span>
                <ExternalLink size={12} className="opacity-80" />
              </a>
            </div>
          </div>

          <button
            onClick={onCompleted}
            className="mt-6 px-6 py-2.5 rounded-xl text-sm font-bold text-white cursor-pointer shadow-sm"
            style={{ background: "#00628F" }}
            data-testid="upload-done-btn"
          >
            Finalizar y Ver Directorio
          </button>
        </div>
      )}
    </ModalShell>
  );
}

// ═══════════════════════════════════════════════════════════
// Invite Single Collaborator Modal — Para RRHH / Administrador
// ═══════════════════════════════════════════════════════════
export function InviteCollaboratorModal({
  client,
  allowedDomains = [],
  onClose,
  onCompleted,
}) {
  const [step, setStep] = useState("form"); // "form" | "success"
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    countryCode: "+507",
    phone: "",
    clientRole: "member", // "member" | "admin"
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const email = formData.email.trim().toLowerCase();
    if (!email || !EMAIL_RE.test(email)) {
      setError("Por favor ingresa un correo electrónico válido.");
      return;
    }

    if (!isEmailDomainAllowed(email, allowedDomains)) {
      setError(
        `El correo no pertenece al dominio corporativo autorizado (${allowedDomains.map((d) => `@${d}`).join(", ")}).`
      );
      return;
    }

    const rawPhone = formData.phone.trim();
    let normalizedPhone = "";
    if (rawPhone) {
      const phoneCheck = validateWhatsAppPhone(rawPhone, formData.countryCode || "+507");
      if (!phoneCheck.valid) {
        setError(phoneCheck.error || "Por favor ingresa un número de teléfono válido.");
        return;
      }
      normalizedPhone = `+${phoneCheck.cleanPhone}`;
    }

    setBusy(true);

    try {
      const cleanPhone = normalizedPhone || normalizeWhatsAppPhone(rawPhone, formData.countryCode || "+507");
      const fullName = `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim() || email;

      // 1. Guardar en Firestore pending_assignments
      const ref = doc(db, "pending_assignments", email);
      await setDoc(ref, {
        email,
        clientId: client.id,
        clientName: client.name,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        phone: cleanPhone,
        clientRole: formData.clientRole,
        invitedAt: new Date().toISOString(),
        invitedVia: "web_admin",
      });

      // 2. Sincronizar hacia WE360 Core Services (Fase 6: COMPLETED_ACTIVE_COACHING)
      try {
        await syncWebUser({
          companyId: client.id,
          businessModel: "B2B",
          users: [
            {
              name: fullName,
              firstName: formData.firstName.trim(),
              lastName: formData.lastName.trim(),
              email,
              phone: cleanPhone || "+50762836600",
              companyId: client.id,
              businessModel: "B2B",
            },
          ],
        });
      } catch (syncErr) {
        console.warn("[InviteCollaboratorModal] Warning syncing single user to Core:", syncErr);
      }

      setStep("success");
    } catch (err) {
      console.error("Error al invitar colaborador:", err);
      setError(err.message || "No se pudo registrar la invitación.");
    } finally {
      setBusy(false);
    }
  };

  const inviteWebUrl = `${typeof window !== "undefined" ? window.location.origin : ""}/register?empresa=${encodeURIComponent(client.id || client.name)}&email=${encodeURIComponent(formData.email)}`;
  const whatsappInviteMessage = `Hola ${formData.firstName || "colaborador"}, te damos la bienvenida al Programa de Salud y Longevidad de ${client.name} en WE360. Para comenzar tu acompañamiento médico y de hábitos con tu Coach de IA, escríbele "Hola" a este chat o ingresa a: ${inviteWebUrl}`;
  const whatsappDirectUrl = getWhatsAppChatUrl(
    (formData.phone ? normalizeWhatsAppPhone(formData.phone, formData.countryCode || "+507") : "") || WE360_WHATSAPP_BOT_NUMBER,
    whatsappInviteMessage
  );

  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteWebUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
  };

  return (
    <ModalShell
      title={`Invitar Colaborador a ${client.name}`}
      subtitle={
        allowedDomains?.length > 0
          ? `Dominio autorizado: ${allowedDomains.map((d) => `@${d}`).join(", ")}`
          : "Pre-aprovisiona a un nuevo colaborador para onboarding inmediato"
      }
      onClose={onClose}
      testId="invite-collaborator-modal"
    >
      {step === "form" ? (
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="invite-collaborator-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Nombre *</label>
              <input
                type="text"
                required
                placeholder="Ej. Ana"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className="w-full px-3 py-2 rounded-xl border border-slate-300 text-sm focus:outline-none focus:border-[#00628F]"
                data-testid="invite-input-firstname"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Apellido</label>
              <input
                type="text"
                placeholder="Ej. Morales"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className="w-full px-3 py-2 rounded-xl border border-slate-300 text-sm focus:outline-none focus:border-[#00628F]"
                data-testid="invite-input-lastname"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1 flex items-center gap-1.5">
              <Mail size={13} className="text-slate-400" /> Correo Electrónico *
            </label>
            <input
              type="email"
              required
              placeholder="ana.morales@empresa.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 rounded-xl border border-slate-300 text-sm focus:outline-none focus:border-[#00628F]"
              data-testid="invite-input-email"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1 flex items-center justify-between">
              <span className="flex items-center gap-1.5">
                <Phone size={13} className="text-slate-400" /> WhatsApp / Teléfono (Recomendado)
              </span>
              <span className="text-[10px] text-[#00628F] font-semibold">Para onboarding sin fricción</span>
            </label>
            <div className="flex gap-2">
              <select
                value={formData.countryCode}
                onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                className="w-[145px] shrink-0 px-2.5 py-2 rounded-xl border border-slate-300 text-xs font-medium bg-white text-slate-700 focus:outline-none focus:border-[#00628F]"
                data-testid="invite-select-country-code"
                aria-label="Código de país"
              >
                {COUNTRY_CODES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label}
                  </option>
                ))}
              </select>
              <input
                type="tel"
                placeholder="6283-6600"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="flex-1 min-w-0 px-3 py-2 rounded-xl border border-slate-300 text-sm focus:outline-none focus:border-[#00628F]"
                data-testid="invite-input-phone"
              />
            </div>
            <p className="text-[10px] text-slate-400 mt-1">
              Selecciona el código de país. Permitirá al colaborador interactuar de inmediato con el Asistente de WhatsApp.
            </p>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Rol en la Empresa</label>
            <select
              value={formData.clientRole}
              onChange={(e) => setFormData({ ...formData, clientRole: e.target.value })}
              className="w-full px-3 py-2 rounded-xl border border-slate-300 text-sm focus:outline-none focus:border-[#00628F] bg-white cursor-pointer"
              data-testid="invite-select-role"
            >
              <option value="member">Colaborador / Empleado</option>
              <option value="admin">Administrador de RRHH / Líder</option>
            </select>
          </div>

          {error && (
            <div className="p-3 rounded-xl text-xs bg-red-50 text-red-800 border border-red-200">
              {error}
            </div>
          )}

          <div className="flex gap-2 pt-2 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-sm font-semibold cursor-pointer bg-slate-100 text-slate-700 hover:bg-slate-200"
              data-testid="invite-cancel-btn"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={busy}
              className="px-5 py-2 rounded-xl text-sm font-bold text-white shadow-sm flex items-center gap-2 cursor-pointer"
              style={{ background: "#00628F", opacity: busy ? 0.7 : 1 }}
              data-testid="invite-submit-btn"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
              <span>Pre-aprovisionar e Invitar</span>
            </button>
          </div>
        </form>
      ) : (
        <div className="text-center py-4" data-testid="invite-success-view">
          <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-3">
            <CheckCircle2 size={28} />
          </div>
          <h3 className="text-lg font-extrabold text-slate-900">¡Colaborador Invitado con Éxito!</h3>
          <p className="text-xs text-slate-500 mt-1">
            <strong>{formData.firstName} {formData.lastName}</strong> ({formData.email}) quedó registrado y sincronizado en Core Services en <strong>Fase 6</strong>.
          </p>

          <div className="mt-5 p-4 rounded-2xl bg-slate-50 border border-slate-200 text-left space-y-3">
            <p className="text-xs font-bold text-slate-700 uppercase tracking-wide">
              Envío directo de credenciales y acceso:
            </p>

            <a
              href={whatsappDirectUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-xs font-bold text-white bg-[#25D366] hover:bg-[#20ba5a] transition-all shadow-sm cursor-pointer"
              data-testid="invite-send-whatsapp-btn"
            >
              <MessageSquare size={16} />
              <span>Enviar Invitación por WhatsApp</span>
              <ExternalLink size={13} className="opacity-80" />
            </a>

            <button
              type="button"
              onClick={handleCopyLink}
              className="w-full flex items-center justify-center gap-2 py-2 px-4 rounded-xl text-xs font-bold border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 transition-all cursor-pointer"
              data-testid="invite-copy-link-btn"
            >
              {copiedLink ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
              <span>{copiedLink ? "¡Enlace Copiado al Portapapeles!" : "Copiar Enlace de Registro Web"}</span>
            </button>
          </div>

          <div className="flex gap-2 mt-5 justify-center">
            <button
              type="button"
              onClick={() => {
                setFormData({ firstName: "", lastName: "", email: "", phone: "", clientRole: "member" });
                setStep("form");
              }}
              className="px-4 py-2 rounded-xl text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 cursor-pointer"
              data-testid="invite-another-btn"
            >
              Invitar a otro colaborador
            </button>
            <button
              type="button"
              onClick={onCompleted}
              className="px-5 py-2 rounded-xl text-xs font-bold text-white cursor-pointer"
              style={{ background: "#00628F" }}
              data-testid="invite-finish-btn"
            >
              Listo
            </button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

// ─── Shared sub-components ─────────────────────────────────
function ModalShell({ title, subtitle, onClose, children, testId, wide }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15,23,42,0.55)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid={testId}
    >
      <div className="bg-white rounded-3xl shadow-2xl w-full overflow-hidden" style={{ maxWidth: wide ? 720 : 500, maxHeight: "92vh", display: "flex", flexDirection: "column" }}>
        <div className="px-6 py-4 border-b flex items-center justify-between shrink-0" style={{ borderColor: "#E2E8F0" }}>
          <div className="min-w-0">
            <h3 className="text-base font-extrabold truncate text-slate-900">{title}</h3>
            {subtitle && <p className="text-xs truncate text-slate-500 mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-xl hover:bg-slate-100 cursor-pointer shrink-0 border-none bg-transparent">
            <X size={18} className="text-slate-500" />
          </button>
        </div>
        <div className="px-6 py-5 overflow-y-auto" style={{ flex: 1 }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function Section({ title, color, icon: Icon, children }) {
  return (
    <div>
      <div className="px-3 py-2 flex items-center gap-2 bg-slate-50 border-t border-slate-200">
        <Icon size={13} style={{ color }} />
        <span className="text-xs font-bold text-slate-800">{title}</span>
      </div>
      {children}
    </div>
  );
}

function Row({ email, extra }) {
  return (
    <div className="px-3 py-2 border-t border-slate-100 flex items-center justify-between">
      <span className="text-sm text-slate-800">{email}</span>
      {extra && <span className="text-[11px] text-slate-400">{extra}</span>}
    </div>
  );
}

function Stat({ label, value, color, testId }) {
  return (
    <div className="rounded-xl p-2.5 text-center" style={{ background: `${color}15` }} data-testid={testId}>
      <div className="text-lg font-black" style={{ color }}>{value}</div>
      <div className="text-[10px] uppercase font-bold tracking-wide" style={{ color }}>{label}</div>
    </div>
  );
}

function ResultRow({ label, value, color }) {
  return (
    <div className="flex items-center justify-between p-2 rounded-xl" style={{ background: `${color}10` }}>
      <span className="text-slate-600">{label}</span>
      <strong style={{ color }}>{value}</strong>
    </div>
  );
}
